/* The front page. ONE component, two states, and the off-air one is not the
 * afterthought.
 *
 * We are on air maybe six hours a day. For the other eighteen this page is the
 * station, and the single worst thing it could do is look broken — a dead play
 * button, an empty hero, or Noods' "Sorry, our schedule is currently out of
 * action", which is a sentence that tells a listener nothing except that
 * somebody stopped maintaining the site. Operator Radio gets this right: it
 * says what the stream is serving right now and when the next live show starts,
 * and both of those are facts we already have in /api/live.
 *
 * So the off-air state carries three things, in this order:
 *   1. OFF AIR, as a word and a strike rule, in the same rectangle and the same
 *      position the ON AIR badge occupies. Nothing moves between states.
 *   2. A live countdown to the next booking, run against `server_time` so a
 *      laptop four minutes fast does not promise a show that has not started.
 *   3. An honest statement of what the stream is actually playing — the
 *      archive-shuffle label AND the set currently on it. The play button works
 *      in both states because /offair is always up (D7).
 *
 * THE GRID BREAK. The live hero hangs its sleeve 32px past the bottom of the
 * blue band, over the NEXT ON row. It is the one deliberate break on this
 * screen; everything else lines up on the same rules, which is what makes that
 * one overlap read as intent instead of as a bug. The NEXT ON row reserves the
 * room on its right so nothing ends up underneath the sleeve.
 *
 * Nav, ticker, footer and the player bar are App.tsx's — they wrap every route
 * and must not be re-mounted here. This file owns what sits between them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, isAbortError } from '../api';
import { LIVE_ARTWORK } from '../brand';
import { useChat } from '../chat';
import {
  Artwork,
  Chip,
  ChipList,
  Countdown,
  Empty,
  EpisodeCardLink,
  ErrorNote,
  OnAirBadge,
  ScheduleRow,
  Section,
  Spinner,
  hostNames,
  slotHostNames,
} from '../components';
import {
  STATION_TZ,
  formatDayDate,
  formatTime,
  formatTimeRange,
  isoDate,
  readTzMode,
  stationToday,
  tzLabel,
} from '../format';
import type { TzMode } from '../format';
import { useLive, usePlayer } from '../player';
import type { EpisodeCard, HostCard, LiveState, SlotCard, UpNext } from '../types';

/* ------------------------------------------------------------------- state */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed'; error: unknown };

const LOADING = { status: 'loading' } as const;

/** How many residents the roster strip draws. Six columns, one row. */
const RESIDENT_COLUMNS = 6;

/** How many cards the Latest grid draws. Four across. */
const LATEST_CARDS = 4;

/**
 * A gap shorter than this is not worth a row of its own. Fifteen minutes
 * between two bookings is a changeover, not an unattended stretch of archive
 * shuffle, and drawing it as one would make the day read as far more broken up
 * than it is.
 */
const MIN_DARK_MS = 15 * 60 * 1000;

/* -------------------------------------------------------- station calendar
 *
 * format.ts renders moments and never does arithmetic on them, which is the
 * right split — but the dark hours need one thing it does not expose: the UTC
 * instant at which a station-local day begins. It is asked of Intl with an
 * explicit zone rather than computed from an offset, because the last Sunday in
 * March is exactly where offset arithmetic breaks and it breaks silently.
 */

function zoneOffsetMs(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));

  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? Number.NaN);

  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
  return Number.isFinite(asIfUtc) ? asIfUtc - at : 0;
}

/** The UTC instant of midnight, station time, on a `YYYY-MM-DD`. */
function stationMidnight(date: string): number {
  const guess = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(guess)) return Number.NaN;
  // Two passes: the first lands within an hour, the second is exact even when
  // the guess and the answer sit on opposite sides of a clock change.
  const once = guess - zoneOffsetMs(guess, STATION_TZ);
  return guess - zoneOffsetMs(once, STATION_TZ);
}

function nextDate(date: string): string {
  const at = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(at)) return date;
  return new Date(at + 86_400_000).toISOString().slice(0, 10);
}

/* --------------------------------------------------------- the dark hours */

interface Segment {
  /** The slot, or null for an unattended stretch. */
  slot: SlotCard | null;
  startsAt: string;
  endsAt: string;
}

/**
 * The archive shuffle, as a row.
 *
 * A synthesised SlotCard rather than a bespoke component: ScheduleRow already
 * knows what `kind: 'fallback'` looks like — the surface field, the muted
 * title, the word "Fallback" in the state column, the 24/7 sleeve — and a
 * second row component that drew the same thing slightly differently is how a
 * schedule stops looking like one printed sheet.
 *
 * The catalogue number is deliberately empty: there is no booking here, and
 * inventing one would put a number on screen that resolves to nothing.
 */
function darkSlot(startsAt: string, endsAt: string): SlotCard {
  return {
    catalogue_no: '',
    title: 'Archive shuffle',
    starts_at: startsAt,
    ends_at: endsAt,
    kind: 'fallback',
    status: 'scheduled',
    is_repeat: false,
    hosts: [],
    genres: [],
    artwork_color: 'deep',
    episode_catalogue_no: null,
  };
}

/**
 * One station day, in order, with nothing hidden.
 *
 * The API returns bookings and NOT gaps, on purpose — the client draws the dark
 * hours, because only the client knows how it wants to say "nobody is in the
 * building". We say it out loud. This station is dark most of the day and a
 * schedule that quietly omits eighteen hours of it is a schedule that lies.
 */
function daySegments(date: string, slots: readonly SlotCard[]): Segment[] {
  const dayStart = stationMidnight(date);
  const dayEnd = stationMidnight(nextDate(date));
  if (!Number.isFinite(dayStart) || !Number.isFinite(dayEnd)) {
    return slots.map((slot) => ({ slot, startsAt: slot.starts_at, endsAt: slot.ends_at }));
  }

  const ordered = [...slots].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));

  const out: Segment[] = [];
  let cursor = dayStart;

  for (const slot of ordered) {
    const start = Date.parse(slot.starts_at);
    const end = Date.parse(slot.ends_at);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    if (start - cursor >= MIN_DARK_MS) {
      out.push({
        slot: null,
        startsAt: new Date(cursor).toISOString(),
        endsAt: new Date(start).toISOString(),
      });
    }
    out.push({ slot, startsAt: slot.starts_at, endsAt: slot.ends_at });
    // A show booked 23:00-01:00 belongs to the day it starts on and takes the
    // first hour of the next day with it; the cursor follows it out of the day.
    cursor = Math.max(cursor, end);
  }

  if (dayEnd - cursor >= MIN_DARK_MS) {
    out.push({
      slot: null,
      startsAt: new Date(cursor).toISOString(),
      endsAt: new Date(dayEnd).toISOString(),
    });
  }

  return out;
}

/* ------------------------------------------------------------------ words */

/** `CEST`, `UTC`, or nothing when the zone has no short name worth printing. */
function zoneWord(tz: TzMode, at?: string): string {
  const label = tzLabel(tz, at);
  if (label === 'UTC') return 'UTC';
  const parts = label.split('·');
  return parts.length > 1 ? parts[parts.length - 1].trim() : '';
}

/** `20:00 — 22:00 CEST · AMS · HGR-053` */
function slotLine(
  startsAt: string,
  endsAt: string,
  tz: TzMode,
  extras: (string | null | undefined)[],
): string {
  const zone = zoneWord(tz, startsAt);
  const when = `${formatTimeRange(startsAt, endsAt, tz)}${zone ? ` ${zone}` : ''}`;
  return [when, ...extras].filter(Boolean).join(' · ');
}

/** True when a moment falls on today's station-local calendar date. */
function isStationToday(iso: string): boolean {
  return isoDate(iso, 'station') === stationToday();
}

function genreHref(slug: string): string {
  return `/genre/${encodeURIComponent(slug)}`;
}

/* ------------------------------------------------------------------ pieces */

/**
 * NEXT ON. Twelve words, and per the research the highest-value element on a
 * station front page: it is the answer to "is it worth coming back tonight".
 *
 * Off air it reads AFTER THAT, because the countdown in the hero has already
 * taken the next booking and printing the same show twice in 200px of page
 * would be furniture rather than information.
 */
function NextOn({
  label,
  item,
  tz,
  underhang,
}: {
  label: string;
  item: UpNext | null;
  tz: TzMode;
  /** Reserve room on the right for the hero sleeve hanging into this row. */
  underhang: boolean;
}) {
  const who = item ? slotHostNames(item.title, item.hosts) : '';

  return (
    <div className={underhang ? 'nexton nexton--underhang' : 'nexton'}>
      <span className="mono dim nexton__lbl">{label} &rarr;</span>
      {item ? (
        <span className="nexton__show">
          {formatTime(item.starts_at, tz)} · {item.title}
          {who ? <span className="nexton__who"> w/ {who}</span> : null}
        </span>
      ) : (
        <span className="mono nexton__none">Nothing else booked yet</span>
      )}
      <Link className="mono nexton__all" to="/schedule">
        Full schedule &rarr;
      </Link>
    </div>
  );
}

/** The blue band. On air, and the only saturated full-bleed field on the page. */
function LiveHero({ state, tz }: { state: LiveState; tz: TzMode }) {
  const { isPlaying, mode, pause, playLive } = usePlayer();
  const { closeChat, open: chatOpen, openChat, panelId, unread } = useChat();
  const now = state.now;
  if (!now) return null;

  const playingLive = mode === 'live' && isPlaying;
  const who = slotHostNames(now.title, now.hosts);

  return (
    <div className="hero on-blue">
      <div className="hero__body">
        <OnAirBadge live />

        <h1 className="t-mega">{now.title}</h1>

        <div className="hero__line">
          {who ? <div className="t-h3">w/ {who}</div> : null}
          <div className="mono-13">
            {slotLine(now.starts_at, now.ends_at, tz, [now.location_short, now.catalogue_no])}
          </div>
        </div>

        {now.genres.length > 0 ? (
          <ChipList label="Genres">
            {now.genres.map((genre) => (
              <Chip key={genre.slug} color={genre.color_token} to={genreHref(genre.slug)}>
                {genre.name}
              </Chip>
            ))}
          </ChipList>
        ) : null}

        <div className="hero__cta">
          <button
            type="button"
            className="btn-lg"
            onClick={playingLive ? pause : playLive}
            aria-label={playingLive ? 'Pause the live stream' : `Tune in to ${now.title}, live now`}
          >
            <span className={playingLive ? 'pausebars' : 'tri'} aria-hidden="true" />
            {playingLive ? 'Pause' : 'Tune in'}
          </button>
          {now.kind === 'live' ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={chatOpen ? closeChat : openChat}
              aria-controls={panelId}
              aria-expanded={chatOpen}
            >
              {chatOpen ? 'Close chat' : 'Join chat'}
              {!chatOpen && unread > 0 ? ` · ${unread}` : ''}
            </button>
          ) : null}
          <span className="mono">
            {state.stream.bitrate} kbps {state.stream.format}
          </span>
        </div>

        {/* A slot is open but nobody is on the harbor. Saying so beats letting
            someone press play into two minutes of silence and conclude we are
            broken. */}
        {!now.dj_connected ? (
          <p className="mono hero__booth">
            Starting shortly — the slot is open and we are waiting on the booth. The stream is
            running either way.
          </p>
        ) : null}
      </div>

      <div className="hero__sleeve">
        <Artwork
          color={now.artwork_color}
          imageUrl={LIVE_ARTWORK}
          catalogueNo={now.catalogue_no}
          size="hero"
        />
      </div>
    </div>
  );
}

/** The eighteen-hours-a-day band. Never an error, never a dead play button. */
function OffAirHero({
  state,
  tz,
  onCountdownElapsed,
}: {
  state: LiveState;
  tz: TzMode;
  onCountdownElapsed: () => void;
}) {
  const next = state.next[0] ?? null;
  const who = next ? slotHostNames(next.title, next.hosts) : '';

  const headline = next
    ? isStationToday(next.starts_at)
      ? `Nothing live until ${formatTime(next.starts_at, tz)}`
      : `Nothing live until ${formatDayDate(next.starts_at, tz)}`
    : 'Nothing live booked yet';

  return (
    <div className="hero-off">
      <div className="hero__body">
        <OnAirBadge live={false} />

        <h1 className="t-mega">{headline}</h1>

        {next ? (
          <div className="countdown">
            <div className="countdown__clock">
              <span className="mono dim">Next show in</span>
              <Countdown
                to={next.starts_at}
                from={state.server_time}
                onElapsed={onCountdownElapsed}
                elapsedLabel="Starting now"
                description={`${next.title} starts at ${formatTime(next.starts_at, tz)} ${tzLabel(
                  tz,
                  next.starts_at,
                )}.`}
              />
            </div>
            <div className="countdown__next">
              <span className="t-h3">
                {next.title}
                {next.kind === 'repeat' ? <span className="row__repeat"> (R)</span> : null}
              </span>
              <span className="mono">
                {slotLine(next.starts_at, next.ends_at, tz, [
                  who ? `w/ ${who}` : null,
                  next.catalogue_no,
                ])}
              </span>
            </div>
          </div>
        ) : (
          <p className="t-body">
            Nothing is booked in the next fortnight. That is a gap in the calendar rather than a
            fault — we programme one season at a time, and the open call below is how the next one
            gets filled.
          </p>
        )}

        <p className="mono dim hero__note">
          The stream never drops. Between shows it serves the archive shuffle — leave the tab open
          and the next broadcast starts on its own.
        </p>
      </div>

      <div className="hero__sleeve">
        <Artwork color="deep" display="24/7" label="Archive shuffle" size="hero" />
      </div>
    </div>
  );
}

/** What the stream is serving instead. Named, with the set that is on it. */
function DarkBand({ state }: { state: LiveState }) {
  const { isPlaying, mode, pause, playLive } = usePlayer();
  const fallback = state.fallback;
  const playingLive = mode === 'live' && isPlaying;

  /* fallback is nullable on the wire, and a fabricated label here would be the
     one lie this whole screen exists to avoid. */
  if (!fallback) {
    return (
      <div className="darkband on-deep">
        <div className="darkband__copy">
          <span className="mono darkband__eyebrow">Currently streaming</span>
          <p className="t-h2 darkband__title">We are off air</p>
          <p className="t-body">
            The station has not told us what the stream is carrying at the moment. Press play and
            you will hear whatever is on the off-air mount, which is up whether or not this page
            can name it.
          </p>
        </div>
        <button type="button" className="btn-lg darkband__cta" onClick={playingLive ? pause : playLive}>
          <span className={playingLive ? 'pausebars' : 'tri'} aria-hidden="true" />
          {playingLive ? 'Pause' : 'Listen now'}
        </button>
      </div>
    );
  }

  const playing = fallback.playing;
  const who = playing ? hostNames(playing.hosts) : '';

  return (
    <div className="darkband on-deep">
      <div className="darkband__copy">
        <span className="mono darkband__eyebrow">Currently streaming</span>
        <p className="t-h2 darkband__title">{fallback.label}</p>
        {fallback.kind === 'silence' ? (
          <p className="t-body">
            The off-air mount is carrying silence right now. Nothing is wrong at your end — there is
            simply nothing queued, and the next booked show starts on its own.
          </p>
        ) : (
          <p className="t-body">
            Every set in the archive, shuffled, running whenever nobody is live.
            {playing ? (
              <>
                {' '}
                Now:{' '}
                <strong>
                  {playing.title}
                  {who ? ` — ${who}` : ''}
                  {playing.catalogue_no ? `, ${playing.catalogue_no}` : ''}
                </strong>
                .
              </>
            ) : null}
          </p>
        )}
      </div>
      <button type="button" className="btn-lg darkband__cta" onClick={playingLive ? pause : playLive}>
        <span className={playingLive ? 'pausebars' : 'tri'} aria-hidden="true" />
        {playingLive ? 'Pause' : 'Listen now'}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------- page */

export default function Home() {
  const { live, error: liveError, loading: liveLoading, refresh } = useLive();

  const [tz, setTz] = useState<TzMode>(() => readTzMode());
  const [reloads, setReloads] = useState(0);

  const [today, setToday] = useState<Load<SlotCard[]>>(LOADING);
  const [latest, setLatest] = useState<Load<{ results: EpisodeCard[]; total: number }>>(LOADING);
  const [residents, setResidents] = useState<Load<HostCard[]>>(LOADING);

  /* The zone is a station-wide preference, written by the schedule's toggle
     into one shared key. Another tab changing it changes this page too. */
  useEffect(() => {
    const onStorage = () => setTz(readTzMode());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const showing = live?.live ? live.now : null;
    document.title = showing
      ? `On air: ${showing.title} · Higher Ground Radio`
      : 'Higher Ground Radio — Amsterdam';
  }, [live]);

  /* One controller for the three browsing calls. They are independent — a
     failed roster must not blank the schedule — so each settles into its own
     slice and each gets its own sentence on screen. */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    setToday(LOADING);
    setLatest(LOADING);
    setResidents(LOADING);

    api
      .getSchedule({ from: stationToday(), days: 1 }, signal)
      .then((page) => {
        if (signal.aborted) return;
        setToday({ status: 'ready', data: page.days[0]?.slots ?? [] });
      })
      .catch((error: unknown) => {
        if (signal.aborted || isAbortError(error)) return;
        setToday({ status: 'failed', error });
      });

    api
      .getEpisodes({ limit: LATEST_CARDS, sort: 'newest' }, signal)
      .then((page) => {
        if (signal.aborted) return;
        setLatest({ status: 'ready', data: { results: page.results, total: page.total } });
      })
      .catch((error: unknown) => {
        if (signal.aborted || isAbortError(error)) return;
        setLatest({ status: 'failed', error });
      });

    api
      .getHosts({ resident: true, status: 'active' }, signal)
      .then((rows) => {
        if (signal.aborted) return;
        setResidents({ status: 'ready', data: rows });
      })
      .catch((error: unknown) => {
        if (signal.aborted || isAbortError(error)) return;
        setResidents({ status: 'failed', error });
      });

    return () => controller.abort();
  }, [reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  const todayDate = stationToday();

  const segments = useMemo(
    () => (today.status === 'ready' ? daySegments(todayDate, today.data) : []),
    [today, todayDate],
  );

  /* The first booking that has not started yet, so exactly one row says "Up
     next" and the rest say "Scheduled". */
  const nextCatalogueNo = useMemo(() => {
    if (today.status !== 'ready') return null;
    const nowMs = live?.server_time ? Date.parse(live.server_time) : Date.now();
    const upcoming = [...today.data]
      .filter((slot) => slot.status !== 'cancelled' && Date.parse(slot.starts_at) > nowMs)
      .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
    return upcoming[0]?.catalogue_no ?? null;
  }, [today, live]);

  const onAir = Boolean(live?.live && live.now);
  const zone = zoneWord(tz);

  return (
    <div className="home">
      <style href="hgr-home" precedence="page">
        {HOME_CSS}
      </style>

      {/* ------------------------------------------------------------ hero */}

      {live ? (
        onAir ? (
          <LiveHero state={live} tz={tz} />
        ) : (
          <OffAirHero state={live} tz={tz} onCountdownElapsed={refresh} />
        )
      ) : liveLoading ? (
        <div className="strip strip--hero">
          <Spinner label="Asking the station what is on air" />
        </div>
      ) : (
        <ErrorNote what="what is on air" onRetry={refresh} retryLabel="Ask again">
          {liveError ??
            'We could not reach the station to find out what is on air. The stream itself may well still be running — press play in the bar at the bottom of the window.'}
        </ErrorNote>
      )}

      {/* A poll failed but we still hold the last good answer. The page is not
          wrong, it is only getting old, and that is worth one quiet line rather
          than replacing everything above with an apology. */}
      {live && liveError ? (
        <div className="strip strip--stale">
          <p className="mono dim strip__text">
            Last updated {formatTime(live.server_time, tz)}
            {zone ? ` ${zone}` : ''} — we have lost contact with the station since. What is on this
            page is the last thing we were told, and the stream itself may well still be running.
          </p>
          <button type="button" className="btn-ghost" onClick={refresh}>
            Try again
          </button>
        </div>
      ) : null}

      {/* --------------------------------------------------------- next on */}

      {live && !onAir ? <DarkBand state={live} /> : null}

      {live ? (
        <NextOn
          label={onAir ? 'Next on' : 'After that'}
          item={(onAir ? live.next[0] : live.next[1]) ?? null}
          tz={tz}
          underhang={onAir}
        />
      ) : null}

      {/* ----------------------------------------------------------- today */}

      <Section
        title="Today"
        meta={`${formatDayDate(todayDate, tz)}${zone ? ` · All times ${zone}` : ''}`}
      />

      {today.status === 'loading' ? (
        <div className="strip">
          <Spinner label="Loading today's schedule" />
        </div>
      ) : today.status === 'failed' ? (
        <ErrorNote error={today.error} what="today's schedule" onRetry={reload} />
      ) : today.data.length === 0 ? (
        <Empty
          kind="Today"
          title="Nothing is booked today."
          actions={
            <>
              <Link className="btn" to="/schedule">
                The week ahead
              </Link>
              <Link className="btn-ghost" to="/archive">
                Browse the archive
              </Link>
            </>
          }
        >
          The stream is still up — it runs the archive shuffle whenever nobody is live.
        </Empty>
      ) : (
        <ul className="rows">
          {segments.map((segment) =>
            segment.slot ? (
              <ScheduleRow
                key={`slot-${segment.slot.catalogue_no}-${segment.startsAt}`}
                slot={segment.slot}
                tz={tz}
                now={live?.server_time ?? null}
                isNext={segment.slot.catalogue_no === nextCatalogueNo}
                genreHref={genreHref}
                as="li"
              />
            ) : (
              <ScheduleRow
                key={`dark-${segment.startsAt}`}
                slot={darkSlot(segment.startsAt, segment.endsAt)}
                tz={tz}
                now={live?.server_time ?? null}
                as="li"
              />
            ),
          )}
        </ul>
      )}

      {/* ---------------------------------------------------------- latest */}

      <Section
        title="Latest"
        meta={
          latest.status === 'ready' ? (
            <Link to="/archive">All {latest.data.total} episodes &rarr;</Link>
          ) : (
            <Link to="/archive">The archive &rarr;</Link>
          )
        }
      />

      {latest.status === 'loading' ? (
        <div className="strip">
          <Spinner label="Loading the latest episodes" />
        </div>
      ) : latest.status === 'failed' ? (
        <ErrorNote error={latest.error} what="the latest episodes" onRetry={reload} />
      ) : latest.data.results.length === 0 ? (
        <Empty
          kind="Archive"
          title="Nothing has been published yet."
          actions={
            <Link className="btn" to="/submit">
              Send us a set
            </Link>
          }
        >
          Every broadcast goes up here after it airs, with public playback on SoundCloud. The first
          one is not far off.
        </Empty>
      ) : (
        <div className="cardgrid">
          {latest.data.results.map((episode) => (
            <EpisodeCardLink
              key={episode.catalogue_no}
              episode={episode}
              tz={tz}
              genreHref={genreHref}
            />
          ))}
        </div>
      )}

      {/* ------------------------------------------------------- open call */}

      <div className="callout">
        <div className="callout__copy">
          <span className="mono">Open call · one-offs and residencies</span>
          <p className="t-h2 callout__title">Send us a set</p>
          <p className="t-body">
            One-off shows and monthly residencies, 60–120 minutes, live from your room or
            pre-recorded. Send a link — SoundCloud, Mixcloud, Bandcamp — not a file. We read
            everything and reply to what fits; we cannot reply to everyone.
          </p>
        </div>
        <Link className="btn-lg callout__cta" to="/submit">
          Submit a set &rarr;
        </Link>
      </div>

      {/* ------------------------------------------------------- residents */}

      <Section
        title="Higher Ground Rhythm Police"
        meta={
          residents.status === 'ready' ? (
            <Link to="/residents">Meet all {residents.data.length} &rarr;</Link>
          ) : (
            <Link to="/residents">The roster &rarr;</Link>
          )
        }
      />

      {residents.status === 'loading' ? (
        <div className="strip">
          <Spinner label="Loading the Higher Ground Rhythm Police" />
        </div>
      ) : residents.status === 'failed' ? (
        <ErrorNote error={residents.error} what="the Higher Ground Rhythm Police" onRetry={reload} />
      ) : residents.data.length === 0 ? (
        <Empty
          kind="Rhythm Police"
          title="Nobody has joined the Rhythm Police yet."
          actions={
            <Link className="btn" to="/submit">
              Apply for a residency
            </Link>
          }
        >
          A residency is a monthly slot for a season. We book them out of the open call, and the
          first intake is still going through.
        </Empty>
      ) : (
        <div className="resgrid">
          {residents.data.slice(0, RESIDENT_COLUMNS).map((host) => (
            <Link
              className="rescell"
              key={host.slug}
              to={`/residents/${encodeURIComponent(host.slug)}`}
            >
              <Artwork
                color={host.artwork_color}
                imageUrl={host.artwork_url}
                display={host.monogram}
                size="card"
              />
              <span className="rescell__name">{host.name}</span>
              <span className="mono dim rescell__meta">
                {host.resident_rank ? `${host.resident_rank} · ` : ''}
                {host.episode_count} {host.episode_count === 1 ? 'set' : 'sets'}
                {host.city ? ` · ${host.city}` : ''}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- css
 *
 * The page-level furniture: the two heroes, the NEXT ON rule, the open-call
 * band and the roster strip. Everything smaller than a band is already in
 * base.css or components.css and is used from there — nothing here restates a
 * chip, a card, a row or a button.
 *
 * Radius 0 throughout. Borders and shadows come only from the two scales in
 * tokens.css, zero blur, one direction. The single 9px shadow is the hover lift
 * base.css already defines for a card, reused so the roster and the archive
 * come off the page by the same amount.
 */
const HOME_CSS = `
.home { --sleeve: clamp(200px, 30vw, 480px); display: flex; flex-direction: column; }

/* ---- live hero ----
   Ink, not ultra blue. This is the largest single field on the site, so it set
   the whole station's temperature — and it was spending a hue to say nothing the
   ON AIR badge and the accent play button do not already say louder. Against ink,
   that accent reads as the only thing on the screen, which is correct: it is. */
.home .hero {
  background: var(--ink); color: var(--on-ink);
  display: flex; gap: clamp(20px, 4vw, 48px);
  padding: 44px clamp(12px, 4vw, 28px) 0;
  position: relative;
  border-bottom: var(--bw) solid var(--edge);
}
.home .hero__body {
  display: flex; flex-direction: column; gap: 22px;
  padding-bottom: 44px; flex: 1 1 auto; min-width: 0;
}
.home .hero .t-mega { color: var(--on-blue); }
.home .hero .mono-13, .home .hero .mono { color: var(--on-blue); }
.home .hero__line { display: flex; flex-direction: column; gap: 10px; }
.home .hero__cta { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-top: 6px; }
.home .hero__booth { max-width: 56ch; line-height: 1.7; margin: 0; }

/* The one deliberate grid break: the sleeve hangs 32px past the band and sits
   over the NEXT ON row, which reserves the room for it on the right. */
.home .hero__sleeve {
  width: var(--sleeve); flex-shrink: 0;
  margin-bottom: -32px; position: relative; z-index: 2;
}

/* ---- off-air hero: same rectangle, same position, different word ---- */
.home .hero-off {
  background: var(--paper);
  display: flex; gap: clamp(20px, 4vw, 48px);
  padding: 44px clamp(12px, 4vw, 28px) 40px;
  border-bottom: var(--bw) solid var(--edge);
}
.home .hero-off .hero__body { padding-bottom: 0; gap: 24px; }
.home .hero-off .hero__sleeve { width: clamp(200px, 26vw, 400px); margin-bottom: 0; }
.home .hero__note { max-width: 60ch; line-height: 1.7; margin: 0; }

.home .countdown {
  border: var(--bw) solid var(--edge); background: var(--paper);
  box-shadow: var(--sh-card); padding: 20px 24px;
  display: flex; align-items: center; gap: 32px; max-width: 660px; flex-wrap: wrap;
}
.home .countdown__clock { display: flex; flex-direction: column; gap: 8px; }
.home .countdown__next {
  border-left: var(--bw) solid var(--edge); padding-left: 28px;
  display: flex; flex-direction: column; gap: 8px; min-width: 0;
}

/* ---- what the stream is serving instead ---- */
.home .darkband {
  background: var(--deep); color: var(--on-deep);
  border-bottom: var(--bw) solid var(--edge);
  padding: 32px clamp(12px, 4vw, 28px);
  display: flex; gap: clamp(20px, 4vw, 48px); align-items: center; flex-wrap: wrap;
}
.home .darkband__copy { flex: 1 1 380px; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.home .darkband .t-body { color: var(--on-deep); }
.home .darkband__eyebrow { color: var(--accent); }
.home .darkband__title { margin: 0; font-size: clamp(1.75rem, 4vw, 2.75rem); }
.home .darkband__cta { flex-shrink: 0; }

/* ---- NEXT ON ---- */
.home .nexton {
  display: flex; align-items: center; gap: clamp(12px, 2vw, 22px);
  padding: 20px clamp(12px, 4vw, 28px);
  border-bottom: var(--bw) solid var(--edge);
  background: var(--paper); flex-wrap: wrap; min-height: 72px;
}
.home .nexton--underhang { padding-right: calc(var(--sleeve) + clamp(28px, 5vw, 76px)); }
.home .nexton__lbl { flex-shrink: 0; }
.home .nexton__show {
  font-size: clamp(1.125rem, 2.4vw, 1.625rem); line-height: 1; letter-spacing: -.01em;
  font-variation-settings: 'wdth' 100, 'wght' 800; text-transform: uppercase; min-width: 0;
}
.home .nexton__who { font-variation-settings: 'wght' 500; }
.home .nexton__all { margin-left: auto; flex-shrink: 0; min-height: 44px; display: inline-flex; align-items: center; }

/* ---- the day, as a list ---- */
.home .rows { list-style: none; margin: 0; padding: 0; }
.home .rows > :last-child { border-bottom: var(--bw) solid var(--edge); }

.home .cardgrid { padding-top: 0; padding-bottom: 0; }

/* ---- open call ---- */
.home .callout {
  background: var(--magenta); color: var(--on-magenta);
  border-top: var(--bw) solid var(--edge); border-bottom: var(--bw) solid var(--edge);
  padding: 38px clamp(12px, 4vw, 28px); margin-top: 26px;
  display: flex; gap: clamp(20px, 5vw, 56px); align-items: center; flex-wrap: wrap;
}
.home .callout__copy { flex: 1 1 420px; display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.home .callout .t-body { color: var(--on-magenta); }
.home .callout__title { margin: 0; font-size: clamp(2rem, 5vw, 3.25rem); }
.home .callout .callout__cta { background: var(--paper); color: var(--text); flex-shrink: 0; }

/* ---- roster strip ---- */
.home .resgrid {
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0; padding: 0 clamp(12px, 4vw, 28px);
}
.home .rescell {
  border: var(--bw) solid var(--edge); margin: 0 -1.5px; background: var(--paper);
  display: flex; flex-direction: column; text-decoration: none; min-width: 0;
  transition: transform var(--dur) linear, box-shadow var(--dur) linear;
}
.home .rescell .art { border: 0; border-bottom: var(--bw) solid var(--edge); }
.home .rescell__name {
  padding: 10px 12px 2px; font-size: .9375rem; line-height: 1.05;
  font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
}
.home .rescell__meta { padding: 0 12px 10px; }
.home .rescell:hover { transform: translate(-3px, -3px); box-shadow: 9px 9px 0 var(--edge); z-index: 1; }
.home .rescell:active { transform: translate(3px, 3px); box-shadow: none; }

/* ---- loading / stale strips ---- */
.home .strip {
  padding: 24px clamp(12px, 4vw, 28px);
  border-bottom: var(--bw) solid var(--edge);
}
.home .strip--hero { padding: 44px clamp(12px, 4vw, 28px); }
.home .strip--stale {
  background: var(--surface);
  display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
}
.home .strip__text { margin: 0; line-height: 1.7; max-width: 68ch; flex: 1 1 320px; }

@media (max-width: 900px) {
  .home .hero, .home .hero-off { padding: 24px clamp(12px, 4vw, 28px) 28px; }
  .home .hero__sleeve { display: none; }
  .home .hero__body { padding-bottom: 0; }
  .home .nexton--underhang { padding-right: clamp(12px, 4vw, 28px); }
  .home .resgrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 620px) {
  .home .countdown { gap: 18px; padding: 16px 16px; }
  .home .countdown__next {
    border-left: 0; border-top: var(--bw) solid var(--edge);
    padding-left: 0; padding-top: 18px; width: 100%;
  }
  .home .resgrid { grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 0 12px; }
  .home .callout { padding: 24px 12px; }
  .home .darkband { padding: 24px 12px; }
}
`;
